import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  styled,
  Textarea,
} from '@mui/joy';
import SvgIcon from '@mui/joy/SvgIcon';
import { Divider } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useBranches from '../../../hooks/usebranches';
import { createNewEvent } from '../../../redux/actions/eventActions';
import { putEvent } from '../../../redux/services/eventsService';
import { uploadFileNoLibrary } from '../../../redux/services/libraryServices';
import { Event } from '../../../types/events/event';
import { NewEvent, newEventSchema } from './schema';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

export type EventFormProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  event?: Event;
};

export const EventForm = (props: EventFormProps) => {
  const { open, setOpen, event } = props;
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    getValues,
  } = useForm<NewEvent>({
    resolver: zodResolver(newEventSchema),
  });

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const [loading, setLoading] = useState(false);
  const branchQuery = useBranches();

  const onSubmit = async (data: NewEvent) => {
    try {
      setLoading(true);
      let imageUrl = null;
      if (data.image && data.image.length > 0) {
        imageUrl = await uploadFileNoLibrary(data.image[0]);
      }

      // Override dates with unix timestamps
      data.startDate = new Date(data.startDate).toISOString();
      data.endDate = data.endDate ? new Date(data.endDate).toISOString() : null;

      const eventToCreate: Partial<Event> = {
        title: data.title,
        description: data.description,
        dateStart: data.startDate,
        dateEnd: data.endDate,
        branch: data.branch,
        isPrivate: data.private,
        imageUrl,
        location: data.location,
        maxAttendees: data.maxAttendees,
        timeEnd: '00:00', // unused fields (combined with dateStart and dateEnd)
        timeStart: '00:00', // unused fields (combined with dateStart and dateEnd)
        type: 'default',
      };

      if (event) {
        // Update existing event
        await putEvent({
          ...eventToCreate,
          _id: event._id,
          imageUrl: imageUrl || event.imageUrl,
        });
      } else {
        // Create new event
        await createNewEvent(eventToCreate);
      }

      reset();
      setOpen(false);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // check event title so we dont reset when event is already set (bit hacky, but eh)
    if (event && event?.title) {
      // set  date locale to nl
      const startDate = dayjs(event.dateStart)
        .locale('nl')
        .format('YYYY-MM-DDTHH:mm');
      const endDate = event.dateEnd
        ? dayjs(event.dateEnd).locale('nl').format('YYYY-MM-DDTHH:mm')
        : null;

      reset({
        title: event.title,
        description: event.description,
        branch: event.branch,
        location: event.location,
        startDate,
        endDate,
        maxAttendees: event.maxAttendees,
        private: event.isPrivate,
      });
    }
  }, [event]);

  if (!branchQuery.isSuccess) {
    return <div>Loading...</div>;
  }

  const branches = branchQuery.data;
  const branchOptions = branches.map((branch) => ({
    value: branch.name, // This should be _id, but backend works with branch name instead. TODO: Make backend filters work with branch _id's
    label: branch.name,
  }));

  return (
    <Modal
      aria-labelledby="close-modal-title"
      open={open}
      onClose={() => setOpen(false)}
    >
      <ModalDialog size="md" sx={{ marginLeft: '5rem' }}>
        <DialogTitle>
          {event ? 'Evenement aanpassen' : 'Nieuw evenement aanmaken'}
        </DialogTitle>
        <DialogContent>Vul de velden in</DialogContent>
        <ModalClose variant="outlined" />
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <fieldset disabled={loading}>
            <Stack direction={'row'} gap={3}>
              {/* Left column */}
              <Stack gap={1}>
                {/* Title */}
                <FormControl error={!!errors.title}>
                  <FormLabel>Titel*</FormLabel>
                  <Input
                    type="text"
                    placeholder="Titel"
                    {...register('title')}
                  />
                  {errors.title && (
                    <FormHelperText>{errors.title.message}</FormHelperText>
                  )}
                </FormControl>

                {/* Description */}
                <FormControl error={!!errors.description}>
                  <FormLabel>Beschrijving</FormLabel>
                  <Textarea
                    placeholder="Beschrijving"
                    {...register('description')}
                    minRows={10}
                    maxRows={10}
                    sx={{ width: '30rem' }}
                  />
                  {errors.description && (
                    <FormHelperText>
                      {errors.description.message}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Image */}
                <FormControl error={!!errors.image}>
                  <FormLabel>Foto*</FormLabel>
                  <Button
                    component="label"
                    role={undefined}
                    tabIndex={-1}
                    variant="outlined"
                    color="neutral"
                    startDecorator={
                      <SvgIcon>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                          />
                        </svg>
                      </SvgIcon>
                    }
                  >
                    {getValues().image
                      ? getValues()?.image[0]?.name
                      : 'Kies een foto'}
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      {...register('image', { required: 'Foto is verplicht' })}
                    />
                  </Button>
                  {errors.image && (
                    <FormHelperText>
                      {errors.image?.message?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
              </Stack>

              <Divider orientation="vertical" flexItem />

              {/* Right column */}
              <Stack spacing={1} justifyContent={'space-between'}>
                {/* Branch */}
                <FormControl error={!!errors.branch}>
                  <FormLabel>Vestiging</FormLabel>
                  <Controller
                    name="branch"
                    control={control}
                    render={({ field }) => (
                      <Select
                        placeholder="Selecteer vestiging"
                        value={field.value || null}
                        onChange={(event, newValue) => {
                          field.onChange(newValue);
                        }}
                      >
                        {branchOptions.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.branch && (
                    <FormHelperText>{errors.branch?.message}</FormHelperText>
                  )}
                </FormControl>

                {/* Locatie */}
                <FormControl error={!!errors.location}>
                  <FormLabel>Locatie*</FormLabel>
                  <Input
                    type="text"
                    placeholder="Locatie"
                    {...register('location')}
                  />
                  {errors.location && (
                    <FormHelperText>{errors.location.message}</FormHelperText>
                  )}
                </FormControl>

                {/* Start date */}
                <FormControl error={!!errors.startDate}>
                  <FormLabel>Startdatum*</FormLabel>
                  <Input type="datetime-local" {...register('startDate')} />
                  {errors.startDate && (
                    <FormHelperText>
                      {errors.startDate?.message?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* End date */}
                <FormControl error={!!errors.endDate}>
                  <FormLabel>Einddatum (optioneel)</FormLabel>
                  <Input type="datetime-local" {...register('endDate')} />
                  {errors.endDate && (
                    <FormHelperText>
                      {errors.endDate?.message?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Max attendees */}
                <FormControl error={!!errors.maxAttendees}>
                  <FormLabel>Maximale deelnemers</FormLabel>
                  <Input
                    type="number"
                    placeholder="Max aantal deelnemers"
                    {...register('maxAttendees')}
                  />
                </FormControl>
                {/* Private checkbox */}
                <FormControl>
                  <FormLabel>Privé?</FormLabel>
                  <Checkbox {...register('private')} />
                  {errors.private && (
                    <FormHelperText>
                      {errors.private?.message?.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
              </Stack>
            </Stack>

            <Box sx={{ marginY: '3rem' }} />
            <Stack justifyContent={'end'} direction={'row'} gap={1}>
              <Button
                color="neutral"
                variant="soft"
                onClick={() => setOpen(false)}
                loading={loading}
              >
                Annuleren
              </Button>
              <Button loading={loading} type="submit">
                {event ? 'Evenement aanpassen' : 'Nieuw evenement aanmaken'}
              </Button>
            </Stack>
          </fieldset>
        </form>
      </ModalDialog>
    </Modal>
  );
};
